import { useState } from 'react'
import {
  MultiSelect,
  Card,
  Group,
  Text,
  Image,
  Stack,
  Button,
  ActionIcon,
  Grid,
  Alert,
  Loader,
  ScrollArea
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconX, IconPhoto } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  assignCreativesToCampaign,
  getCampaignCreatives,
  removeCampaignCreative,
  getAvailableCreativesForCampaign
} from 'supabase/campaigns'
import { Database } from 'types/supabase'
import { useAppTranslation } from 'hooks/useTranslation'

type Creative = Database['public']['Tables']['creatives']['Row']

interface CampaignCreativeAssignmentProps {
  campaignId: string
  advertiserId: string
  onUpdate?: () => void
}

export function CampaignCreativeAssignment({
  campaignId,
  advertiserId,
  onUpdate
}: CampaignCreativeAssignmentProps) {
  const { t } = useAppTranslation()
  const [selectedCreatives, setSelectedCreatives] = useState<string[]>([])

  const { data: availableCreatives, isLoading: loadingAvailable } = useQuery({
    queryKey: ['available-creatives', advertiserId],
    queryFn: () => getAvailableCreativesForCampaign(advertiserId)
  })

  const { data: assignedCreatives, isLoading: loadingAssigned, refetch: refetchAssigned } = useQuery({
    queryKey: ['campaign-creatives', campaignId],
    queryFn: () => getCampaignCreatives(campaignId)
  })

  const assignMutation = useMutation({
    mutationFn: (creativeIds: string[]) =>
      assignCreativesToCampaign(campaignId, creativeIds),
    onSuccess: () => {
      notifications.show({
        title: t('campaigns.creatives_assigned'),
        message: t('campaigns.creatives_assigned_success'),
        color: 'green'
      })
      refetchAssigned()
      setSelectedCreatives([])
      onUpdate?.()
    },
    onError: () => {
      notifications.show({
        title: t('common.error'),
        message: t('campaigns.creatives_assign_error'),
        color: 'red'
      })
    }
  })

  const removeMutation = useMutation({
    mutationFn: (creativeId: string) =>
      removeCampaignCreative(campaignId, creativeId),
    onSuccess: () => {
      notifications.show({
        title: t('campaigns.creative_removed'),
        message: t('campaigns.creative_removed_success'),
        color: 'green'
      })
      refetchAssigned()
      onUpdate?.()
    },
    onError: () => {
      notifications.show({
        title: t('common.error'),
        message: t('campaigns.creative_remove_error'),
        color: 'red'
      })
    }
  })

  const handleAssignCreatives = async () => {
    const currentAssigned = assignedCreatives?.data?.map(item => item.creative_id) || []
    const newCreatives = selectedCreatives.filter(id => !currentAssigned.includes(id))

    if (newCreatives.length === 0) {
      notifications.show({
        title: t('campaigns.no_new_creatives'),
        message: t('campaigns.select_new_creatives'),
        color: 'yellow'
      })
      return
    }

    assignMutation.mutate(newCreatives)
  }

  const selectOptions = availableCreatives?.data?.map((creative: Creative) => ({
    value: creative.id,
    label: creative.title || t('creatives.untitled')
  })) || []

  if (loadingAvailable || loadingAssigned) {
    return (
      <Card>
        <Group justify='center' p='xl'>
          <Loader />
        </Group>
      </Card>
    )
  }

  const assignedCreativesList = assignedCreatives?.data?.filter(item => item.creatives) || []

  return (
    <Stack gap='md'>
      <Card>
        <Stack gap='md'>
          <Text fw={500}>{t('campaigns.assign_creatives')}</Text>

          <MultiSelect
            data={selectOptions}
            value={selectedCreatives}
            onChange={setSelectedCreatives}
            searchable
            clearable
            placeholder={t('campaigns.select_creatives')}
            nothingFoundMessage={t('common.nothing_found')}
            maxDropdownHeight={300}
          />

          <Button
            onClick={handleAssignCreatives}
            loading={assignMutation.isPending}
            disabled={selectedCreatives.length === 0}
          >
            {t('campaigns.assign_selected')}
          </Button>
        </Stack>
      </Card>

      <Card>
        <Stack gap='md'>
          <Text fw={500}>{t('campaigns.assigned_creatives')}</Text>

          {assignedCreativesList.length === 0 ? (
            <Alert icon={<IconPhoto />} color='gray'>
              {t('campaigns.no_creatives_assigned')}
            </Alert>
          ) : (
            <ScrollArea h={400}>
              <Grid>
                {assignedCreativesList.map((assignment) => {
                  const creative = assignment.creatives as any
                  if (!creative) return null

                  return (
                    <Grid.Col key={assignment.id} span={{ base: 12, sm: 6, md: 4 }}>
                      <Card withBorder>
                        <Stack gap='xs'>
                          {creative.card_image_url && (
                            <Image
                              src={creative.card_image_url}
                              alt={creative.title}
                              height={120}
                              fit='cover'
                              radius='sm'
                            />
                          )}
                          <Text size='sm' fw={500} lineClamp={1}>
                            {creative.title || t('creatives.untitled')}
                          </Text>
                          <Group justify='flex-end'>
                            <ActionIcon
                              color='red'
                              variant='subtle'
                              onClick={() => removeMutation.mutate(creative.id)}
                              loading={removeMutation.isPending}
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          </Group>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  )
                })}
              </Grid>
            </ScrollArea>
          )}
        </Stack>
      </Card>
    </Stack>
  )
}